import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios'
import { theme } from '../../styles/theme'
import { useTheme } from '../../contexts/ThemeContext'
import { MobileNav, BottomNav, HamburgerButton, useResponsive } from './MobileNav'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

interface NavItem {
  label: string
  path: string
  children?: { label: string; path: string }[]
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/' },
  { label: 'Markets', path: '/markets' },
  {
    label: 'Analytics',
    path: '/analytics',
    children: [
      { label: 'Product Categories', path: '/analytics/categories' },
      { label: 'Product Sectors', path: '/analytics/sectors' },
      { label: 'Quarterly Reports', path: '/analytics/quarterly' },
      { label: 'Seller Performance', path: '/analytics/sellers' },
      { label: 'Geographic Analysis', path: '/analytics/geographic' },
      { label: 'Market Performance', path: '/analytics/market-performance' },
      { label: 'Inventory Summary', path: '/analytics/inventory' },
    ]
  },
  {
    label: 'Compliance',
    path: '/compliance',
    children: [
      { label: 'Overview', path: '/compliance' },
      { label: 'VAT & Tax', path: '/compliance/vat' },
      { label: 'Product Standards', path: '/compliance/product-standards' },
      { label: 'Customs & Import', path: '/compliance/customs' },
      { label: 'Data Protection', path: '/compliance/data-protection' },
    ]
  },
  {
    label: 'Customer',
    path: '/customer',
    children: [
      { label: 'Customer Journey', path: '/customer-journey' },
      { label: 'Voice of Customer', path: '/voice-of-customer' },
      { label: 'NPS Tracking', path: '/nps-tracking' },
      { label: 'Customer Health', path: '/customer-health' },
      { label: 'Churn Prediction', path: '/churn-prediction' },
      { label: 'Support Analytics', path: '/support-analytics' },
      { label: 'Customer Success', path: '/customer-success' },
      { label: 'Experience Analytics', path: '/experience-analytics' },
    ]
  },
  {
    label: 'Revenue & Sales',
    path: '/revenue',
    children: [
      { label: 'Revenue Analytics', path: '/revenue-analytics-2' },
      { label: 'Sales Pipeline', path: '/sales-pipeline' },
      { label: 'Deal Analytics', path: '/deal-analytics' },
      { label: 'Pricing Analytics', path: '/pricing-analytics' },
      { label: 'Commission Tracking', path: '/commission-tracking' },
      { label: 'Revenue Forecasting', path: '/revenue-forecasting' },
      { label: 'Billing Analytics', path: '/billing-analytics' },
    ]
  },
  {
    label: 'Marketing',
    path: '/marketing',
    children: [
      { label: 'Campaign Analytics', path: '/campaign-analytics' },
      { label: 'Lead Scoring', path: '/lead-scoring' },
      { label: 'Marketing Attribution', path: '/marketing-attribution' },
      { label: 'Content Performance', path: '/content-performance' },
      { label: 'Social Analytics', path: '/social-analytics' },
      { label: 'Email Analytics', path: '/email-analytics' },
      { label: 'SEO Performance', path: '/seo-performance' },
      { label: 'PPC Analytics', path: '/ppc-analytics' },
      { label: 'Marketing ROI', path: '/marketing-roi' },
    ]
  },
  {
    label: 'Operations',
    path: '/operations',
    children: [
      { label: 'Process Automation', path: '/process-automation' },
      { label: 'Quality Management', path: '/quality-management' },
      { label: 'Supply Chain', path: '/supply-chain-analytics' },
      { label: 'Logistics Optimization', path: '/logistics-optimization' },
      { label: 'Inventory Optimization', path: '/inventory-optimization' },
      { label: 'Capacity Planning', path: '/capacity-planning' },
      { label: 'Asset Tracking', path: '/asset-tracking' },
    ]
  },
  {
    label: 'Data & AI',
    path: '/data-ai',
    children: [
      { label: 'Data Catalog', path: '/data-catalog' },
      { label: 'Data Quality', path: '/data-quality' },
      { label: 'Model Registry', path: '/model-registry' },
      { label: 'Model Training', path: '/model-training' },
      { label: 'Model Deployment', path: '/model-deployment' },
      { label: 'ML Pipeline', path: '/ml-pipeline' },
      { label: 'AutoML', path: '/automl' },
      { label: 'MLOps Dashboard', path: '/mlops-dashboard' },
    ]
  },
  {
    label: 'Integration',
    path: '/integration',
    children: [
      { label: 'API Marketplace', path: '/api-marketplace' },
      { label: 'Connector Library', path: '/connector-library' },
      { label: 'Data Sync', path: '/data-sync' },
      { label: 'ETL Builder', path: '/etl-builder' },
      { label: 'Integration Monitoring', path: '/integration-monitoring' },
      { label: 'API Analytics', path: '/api-analytics' },
    ]
  },
  {
    label: 'Executive',
    path: '/executive',
    children: [
      { label: 'Executive Dashboard', path: '/executive-dashboard-exec' },
      { label: 'Board Reporting', path: '/board-reporting' },
      { label: 'Strategic Planning', path: '/strategic-planning-exec' },
      { label: 'KPI Management', path: '/kpi-management' },
      { label: 'Performance Scorecards', path: '/performance-scorecards' },
      { label: 'Business Reviews', path: '/business-reviews' },
      { label: 'Investment Analysis', path: '/investment-analysis' },
      { label: 'Risk Dashboard', path: '/risk-dashboard' },
      { label: 'Compliance Dashboard', path: '/compliance-dashboard' },
      { label: 'Executive Insights', path: '/executive-insights' },
    ]
  },
  { label: 'Expansion Pathway', path: '/expansion-pathway' },
  { label: 'AI Query', path: '/query' },
  { label: 'Reports', path: '/reports' },
]

interface AIStatus {
  status: 'online' | 'offline' | 'checking'
  model?: string
  latency?: number
}

export const Header: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { mode, colors, toggleTheme } = useTheme()
  const { isMobile, isTablet } = useResponsive()
  const [searchQuery, setSearchQuery] = useState('')
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [aiStatus, setAiStatus] = useState<AIStatus>({ status: 'checking' })
  const [showAiStatus, setShowAiStatus] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  // App version
  const APP_VERSION = '2.0.0'

  // Handle mobile nav open from bottom nav
  const handleOpenMobileNav = useCallback(() => {
    setMobileNavOpen(true)
  }, [])

  useEffect(() => {
    window.addEventListener('openMobileNav', handleOpenMobileNav)
    return () => window.removeEventListener('openMobileNav', handleOpenMobileNav)
  }, [handleOpenMobileNav])

  // Check AI status periodically
  useEffect(() => {
    const checkAiStatus = async () => {
      try {
        const startTime = Date.now()
        const response = await axios.get(`${API_BASE_URL}/health/ready`, { timeout: 15000 })
        const latency = Date.now() - startTime
        const ollamaStatus = response.data?.services?.ollama
        if (ollamaStatus && ollamaStatus.status === 'healthy') {
          setAiStatus({
            status: 'online',
            model: ollamaStatus.model || 'gpt-oss:latest',
            latency: ollamaStatus.latency_ms || latency
          })
        } else if (ollamaStatus) {
          setAiStatus({
            status: 'offline',
            model: ollamaStatus.model || 'gpt-oss:latest',
            latency
          })
        } else {
          setAiStatus({ status: 'offline', model: 'gpt-oss:latest' })
        }
      } catch (err) {
        console.error('AI status check failed:', err)
        setAiStatus({ status: 'offline', model: 'gpt-oss:latest' })
      }
    }

    checkAiStatus()
    const interval = setInterval(checkAiStatus, 30000) // Check every 30 seconds
    return () => clearInterval(interval)
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/query?q=${encodeURIComponent(searchQuery)}`)
    }
  }

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <>
      {/* Mobile Navigation */}
      <MobileNav isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      <header style={{
        backgroundColor: colors.cardBackground,
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: theme.shadows.sm,
        transition: 'background-color 0.3s, border-color 0.3s',
      }}>
        {/* Top Bar */}
        <div style={{
          height: '60px',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '0 12px' : isTablet ? '0 20px' : '0 32px',
          borderBottom: `1px solid ${colors.border}`,
        }}>
          {/* Hamburger Button for Mobile/Tablet */}
          {(isMobile || isTablet) && (
            <HamburgerButton
              isOpen={mobileNavOpen}
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
            />
          )}

          {/* Logo */}
          <div
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              marginRight: isMobile ? '12px' : '32px',
              marginLeft: (isMobile || isTablet) ? '8px' : '0',
            }}
          >
          <div style={{
            width: '36px',
            height: '36px',
            background: theme.ai.gradient,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#FFFFFF',
          }}>
            E
          </div>
          <span style={{
            fontSize: '18px',
            fontWeight: 600,
            color: colors.textPrimary,
          }}>
            Expandly AI
          </span>
        </div>

        {/* Global Search - Hidden on mobile, compact on tablet */}
        <form onSubmit={handleSearch} style={{
          flex: 1,
          maxWidth: isMobile ? '0' : isTablet ? '300px' : '500px',
          display: isMobile ? 'none' : 'block',
        }}>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
        }}>
          <span style={{
            position: 'absolute',
            left: '16px',
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            🔍
          </span>
          <input
            type="text"
            placeholder="Search or ask a question... (Ctrl+K)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              height: '44px',
              paddingLeft: '48px',
              paddingRight: '16px',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '10px',
              fontSize: '14px',
              outline: 'none',
              transition: 'border 0.2s',
            }}
            onFocus={(e) => e.target.style.borderColor = theme.colors.primary}
            onBlur={(e) => e.target.style.borderColor = theme.colors.border}
          />
        </div>
      </form>

        <div style={{ flex: 1 }} />

        {/* AI Status Indicator */}
        <div style={{ position: 'relative', marginRight: '16px' }}>
          <button
            onClick={() => setShowAiStatus(!showAiStatus)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              background: aiStatus.status === 'online' ? '#50C87820' : aiStatus.status === 'offline' ? '#EF444420' : '#F59E0B20',
              border: `1px solid ${aiStatus.status === 'online' ? '#50C878' : aiStatus.status === 'offline' ? '#EF4444' : '#F59E0B'}`,
              borderRadius: '20px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: aiStatus.status === 'online' ? '#50C878' : aiStatus.status === 'offline' ? '#EF4444' : '#F59E0B',
              animation: aiStatus.status === 'checking' ? 'pulse 1.5s infinite' : aiStatus.status === 'online' ? 'none' : 'none',
            }} />
            <span style={{
              fontSize: '12px',
              fontWeight: 500,
              color: aiStatus.status === 'online' ? '#50C878' : aiStatus.status === 'offline' ? '#EF4444' : '#F59E0B',
            }}>
              {aiStatus.status === 'online' ? 'AI Online' : aiStatus.status === 'offline' ? 'AI Offline' : 'Checking...'}
            </span>
          </button>

          {showAiStatus && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              width: '280px',
              backgroundColor: colors.cardBackground,
              border: `1px solid ${colors.border}`,
              borderRadius: '12px',
              boxShadow: theme.shadows.lg,
              padding: '16px',
              zIndex: 1001,
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: colors.textPrimary }}>
                On-Prem AI Status
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.textSecondary }}>Status:</span>
                  <span style={{
                    color: aiStatus.status === 'online' ? '#50C878' : '#EF4444',
                    fontWeight: 600,
                  }}>
                    {aiStatus.status === 'online' ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
                {aiStatus.model && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: colors.textSecondary }}>Model:</span>
                    <span style={{ color: colors.textPrimary }}>{aiStatus.model}</span>
                  </div>
                )}
                {aiStatus.latency && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: colors.textSecondary }}>Latency:</span>
                    <span style={{ color: colors.textPrimary }}>{aiStatus.latency}ms</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.textSecondary }}>Server:</span>
                  <span style={{ color: colors.textPrimary }}>192.168.0.98:11434</span>
                </div>
                <div style={{ borderTop: `1px solid ${colors.border}`, marginTop: '8px', paddingTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.textSecondary }}>Version:</span>
                  <span style={{ color: colors.primary, fontWeight: 600 }}>v{APP_VERSION}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '8px',
            marginRight: '12px',
            borderRadius: '8px',
            transition: 'background 0.2s',
          }}
          onMouseOver={(e) => e.currentTarget.style.background = colors.background}
          onMouseOut={(e) => e.currentTarget.style.background = 'none'}
          title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
        >
          {mode === 'light' ? '🌙' : '☀️'}
        </button>

        {/* Notifications */}
        <div style={{ position: 'relative', marginRight: '12px' }}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              position: 'relative',
              transition: 'background 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.background = colors.background}
            onMouseOut={(e) => e.currentTarget.style.background = 'none'}
          >
            🔔
            <span style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              width: '10px',
              height: '10px',
              backgroundColor: theme.colors.warning,
              borderRadius: '50%',
              border: '2px solid #FFFFFF',
            }} />
          </button>

          {showNotifications && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              width: '320px',
              backgroundColor: colors.cardBackground,
              border: `1px solid ${colors.border}`,
              borderRadius: '12px',
              boxShadow: theme.shadows.lg,
              padding: '16px',
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: colors.textPrimary }}>Notifications</h3>
              <p style={{ color: colors.textSecondary, fontSize: '14px' }}>
                No new notifications
              </p>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              transition: 'background 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.background = colors.background}
            onMouseOut={(e) => e.currentTarget.style.background = 'none'}
          >
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              fontSize: '12px',
              fontWeight: 600,
            }}>
              JD
            </div>
            <span style={{
              fontSize: '14px',
              color: colors.textPrimary,
              fontWeight: 500,
            }}>
              John Doe
            </span>
            <span style={{ fontSize: '10px', color: colors.textSecondary }}>▼</span>
          </button>

          {showUserMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              width: '200px',
              backgroundColor: colors.cardBackground,
              border: `1px solid ${colors.border}`,
              borderRadius: '12px',
              boxShadow: theme.shadows.lg,
              padding: '8px',
            }}>
              <MenuItem label="Profile" onClick={() => navigate('/profile')} />
              <MenuItem label="Settings" onClick={() => navigate('/settings')} />
              <MenuItem label="Admin Panel" onClick={() => navigate('/admin')} />
              <div style={{
                height: '1px',
                background: colors.border,
                margin: '8px 0',
              }} />
              <MenuItem label="Logout" onClick={() => {}} danger />
            </div>
          )}
        </div>
      </div>

      {/* Navigation Bar - Hidden on mobile/tablet */}
      <nav style={{
        height: '48px',
        display: (isMobile || isTablet) ? 'none' : 'flex',
        alignItems: 'center',
        padding: '0 32px',
        gap: '4px',
      }}>
        {navItems.map((item) => (
          <div
            key={item.path}
            style={{ position: 'relative' }}
            onMouseEnter={() => item.children && setActiveDropdown(item.label)}
            onMouseLeave={() => setActiveDropdown(null)}
          >
            <button
              onClick={() => !item.children && navigate(item.path)}
              style={{
                background: isActive(item.path) ? colors.primary + '15' : 'none',
                border: 'none',
                borderBottom: isActive(item.path) ? `2px solid ${colors.primary}` : '2px solid transparent',
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: isActive(item.path) ? 600 : 500,
                color: isActive(item.path) ? colors.primary : colors.textPrimary,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.background = colors.background
                }
              }}
              onMouseOut={(e) => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.background = 'none'
                }
              }}
            >
              {item.label}
              {item.children && <span style={{ fontSize: '10px' }}>▼</span>}
            </button>

            {/* Dropdown Menu */}
            {item.children && activeDropdown === item.label && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                minWidth: '220px',
                backgroundColor: colors.cardBackground,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                boxShadow: theme.shadows.lg,
                padding: '8px',
                zIndex: 1000,
              }}>
                {item.children.map((child) => (
                  <button
                    key={child.path}
                    onClick={() => {
                      navigate(child.path)
                      setActiveDropdown(null)
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: location.pathname === child.path ? colors.primary + '15' : 'none',
                      border: 'none',
                      borderRadius: '6px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: location.pathname === child.path ? colors.primary : colors.textPrimary,
                      transition: 'background 0.2s',
                    }}
                    onMouseOver={(e) => {
                      if (location.pathname !== child.path) {
                        e.currentTarget.style.background = colors.background
                      }
                    }}
                    onMouseOut={(e) => {
                      if (location.pathname !== child.path) {
                        e.currentTarget.style.background = 'none'
                      }
                    }}
                  >
                    {child.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </header>

      {/* Bottom Navigation for Mobile */}
      <BottomNav />
    </>
  )
}

const MenuItem: React.FC<{ label: string; onClick: () => void; danger?: boolean }> = ({
  label,
  onClick,
  danger,
}) => {
  const [isHover, setIsHover] = useState(false)

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      style={{
        width: '100%',
        padding: '10px 12px',
        background: isHover ? theme.colors.background : 'none',
        border: 'none',
        borderRadius: '6px',
        textAlign: 'left',
        cursor: 'pointer',
        fontSize: '14px',
        color: danger ? '#E53E3E' : theme.colors.textPrimary,
        transition: 'background 0.2s',
      }}
    >
      {label}
    </button>
  )
}
